/**
 * Ripple Vertex Shader Effect
 * 
 * WebGL2 shader that creates a ripple distortion effect on the UI canvas content.
 * The shader samples from the UI canvas texture and applies displacement based on
 * ripple waves that propagate outward from click points.
 */

export const RIPPLE_VERTEX_SHADER = `#version 300 es
precision highp float;

const vec2 V[3] = vec2[3](
  vec2(-1.0,-1.0),
  vec2( 3.0,-1.0),
  vec2(-1.0, 3.0)
);

void main() {
  vec2 p = V[gl_VertexID];
  gl_Position = vec4(p, 0.0, 1.0);
}`;

export const RIPPLE_FRAGMENT_SHADER = `#version 300 es
precision highp float;

out vec4 fragColor;

uniform vec2  u_resolution;
uniform float u_time;
uniform int   u_rippleCount;
uniform vec2  u_centers[16];
uniform float u_starts[16];
uniform float u_amplitudes[16]; // Per-ripple amplitude scale (0-1)
uniform vec2  u_bulgeCenter; // Magnifying glass bulge position
uniform float u_bulgeActive; // 1.0 if bulge is active, 0.0 otherwise
uniform sampler2D u_sourceTexture;

// Base amplitude - reduced for subtle, elegant effect (like a thought popping into mind)
const float AMP  = 8.0; // Reduced from 35.0 - much more subtle, elegant
const float BULGE_TO_RIPPLE_AMP = 15.0; // Reduced from 120.0 - subtle initial collapse
const float BASE_K = 0.06;
const float BASE_W = 6.0;
const float RDEC = 0.002;
const float LIFE = 1.5; // Slightly shorter lifetime for more subtle effect
const float COLLAPSE_DURATION = 0.08; // Shorter collapse phase for gentler transition

// Reversed ripple direction + outward expansion
// When starting from bulge collapse, begins with inward displacement (collapsed state)
// then transitions to outward expansion (ripple propagation)
float rippleTerm(vec2 pos, vec2 c, float t) {
  if (t <= 0.0 || t > LIFE) return 0.0;
  float r = length(pos - c);
  float dynamicK = BASE_K * (1.0 + 0.5 * t / LIFE);

  // Start with negative phase offset to create initial inward collapse (like bulge dropping)
  // Phase transitions from -π/2 to positive, making sin() go from -1 to 1
  // This creates the effect of bulge collapsing inward then expanding outward
  // The phase offset makes the ripple start collapsed (inward) and then expand outward
  float phaseOffset = -1.57; // -π/2 radians: sin(-π/2) = -1, starts at maximum inward
  float phase = r * dynamicK + t * BASE_W + phaseOffset;

  // Envelope: starts strong, fades out over time
  float env = (1.0 - (t / LIFE)) * exp(-RDEC*r);

  // sin(phase) starts at -1 (inward collapse) then transitions to +1 (outward expansion)
  float wave = sin(phase);

  // During initial collapse phase, use higher amplitude to match bulge
  // Then transition to normal ripple amplitude
  float amplitude = mix(BULGE_TO_RIPPLE_AMP, AMP, smoothstep(0.0, COLLAPSE_DURATION, t));

  return wave * env * amplitude;
}

// Magnifying glass bulge effect - static dome like apex of ripple
float bulgeTerm(vec2 pos, vec2 c) {
  float r = length(pos - c);
  float bulgeRadius = 250.0; // Increased bulge radius (was 150.0)
  float bulgeStrength = 120.0; // Increased bulge displacement strength (was 80.0)

  // Smooth falloff using inverse distance squared
  // Creates a dome effect that's strongest at center, fades out
  float falloff = 1.0 / (1.0 + (r / bulgeRadius) * (r / bulgeRadius));

  // Apply smooth edge falloff
  if (r > bulgeRadius) {
    falloff = 0.0;
  } else {
    // Smooth edge transition
    float edgeDist = r / bulgeRadius;
    falloff *= smoothstep(1.0, 0.7, edgeDist);
  }

  return falloff * bulgeStrength;
}

void main() {
  vec2 pos = gl_FragCoord.xy;
  vec2 disp = vec2(0.0);

  // Add bulge effect (magnifying glass) if active
  // Magnifying glass pulls pixels toward center (inverse of ripple direction)
  if (u_bulgeActive > 0.5) {
    float bulge = bulgeTerm(pos, u_bulgeCenter);
    vec2 dir = normalize(u_bulgeCenter - pos + vec2(1e-5)); // Inward direction for magnifying effect
    disp += dir * bulge;
  }

  // Add ripple effects
  // Ripples start collapsed inward (negative sin) then expand outward (positive sin)
  // This creates the effect of bulge dropping down and creating a ripple
  for (int i=0; i<16; ++i) {
    if (i >= u_rippleCount) break;
    float t = u_time - u_starts[i];
    float s = rippleTerm(pos, u_centers[i], t);
    
    // Apply per-ripple amplitude scale (intensity multiplier)
    // Allows different intensities for user clicks vs automatic spawns
    float intensity = u_amplitudes[i];
    s *= intensity;

    // When s is negative: ripple is collapsing inward (like bulge)
    // When s is positive: ripple is expanding outward (normal ripple)
    // Direction is always outward, but negative s makes it push inward
    // Amplitude is already included in rippleTerm (scales from bulge to normal)
    vec2 dir = normalize(pos - u_centers[i] + vec2(1e-5));
    disp += dir * s;
  }

  vec2 uv = (pos + disp) / u_resolution;
  uv.y = 1.0 - uv.y;

  // Sample premultiplied content (we upload with UNPACK_PREMULTIPLY_ALPHA_WEBGL)
  vec4 color = texture(u_sourceTexture, uv);

  // No discards; let proper premultiplied blending handle edges
  fragColor = color;
}`;

// Pass-through shader for when no ripples are active
// This ensures we always use the same WebGL rendering path
export const PASSTHROUGH_FRAGMENT_SHADER = `#version 300 es
precision highp float;

out vec4 fragColor;

uniform vec2 u_resolution;
uniform sampler2D u_sourceTexture;

void main() {
  vec2 pos = gl_FragCoord.xy;
  vec2 uv = pos / u_resolution;
  uv.y = 1.0 - uv.y;
  vec4 color = texture(u_sourceTexture, uv);
  fragColor = color;
}`;

export const MAX_RIPPLES = 16;
export const RIPPLE_LIFETIME = 2.0;

